/**
 * Central permissions / capabilities for admin vs staff (and teacher).
 * Use for: sidebar visibility, route guards, hiding create/edit/delete in UI.
 */

import { PageTypeAdmin } from "@/lib/types";
import type { AppUser } from "@/lib/types/next-auth";
import { UserRoles } from "@/lib/types/user-roles";
import type { AdminRoleFlags } from "@/lib/utils/roles-common";

export type AdminFeature =
  | "settings"
  | "facilities"
  | "seasons"
  | "balance"
  | "reports"
  | "employeesDetail"
  | "employeesEdit"
  | "classesEdit"
  | "timeClock"
  | "privateLessonsCalendar";

const STAFF_ALLOWED_PATH_PREFIXES: string[] = [
  PageTypeAdmin.Dashboard,
  PageTypeAdmin.Students,
  PageTypeAdmin.Parents,
  PageTypeAdmin.Employees, // list only; detail blocked separately
  PageTypeAdmin.Appointments,
  PageTypeAdmin.Classes,
  PageTypeAdmin.Policies,
  PageTypeAdmin.PendingEnrollments,
  PageTypeAdmin.WaitlistEnrollments,
  PageTypeAdmin.ParentEnrollmentFollowUp,
  PageTypeAdmin.WithdrawalRequests,
  PageTypeAdmin.MakeupRequests,
  PageTypeAdmin.Notifications,
  PageTypeAdmin.Tasks,
  PageTypeAdmin.TimeClock,
  PageTypeAdmin.Tickets,
  PageTypeAdmin.Resources,
  PageTypeAdmin.Dances,
  PageTypeAdmin.Contractors,
  PageTypeAdmin.Password,
];

/** Paths staff must never access (redirect to /forbidden). Includes billing. */
const STAFF_FORBIDDEN_PATH_PREFIXES: string[] = [
  PageTypeAdmin.Settings,
  PageTypeAdmin.Plugins,
  PageTypeAdmin.Facilities,
  PageTypeAdmin.Seasons,
  PageTypeAdmin.Tuition,
  PageTypeAdmin.Invoices,
  PageTypeAdmin.Charge,
  PageTypeAdmin.Balance,
  PageTypeAdmin.Reports,
  PageTypeAdmin.Locations,
  PageTypeAdmin.ClassRooms,
  PageTypeAdmin.Jobs,
  PageTypeAdmin.Attendance,
  PageTypeAdmin.Availability,
  PageTypeAdmin.PayrollHours,
];

/** Staff can access Employees list but not /employees/[id] */
const STAFF_FORBIDDEN_PATH_PATTERNS: Array<{
  test: (path: string) => boolean;
}> = [{ test: (path) => /^\/admin\/dashboard\/employees\/\d+/.test(path) }];

// ========== Teacher: only these paths (read-only for system data; CRUD only for own time clock, availability, attendance)
const TEACHER_ALLOWED_PATH_PREFIXES: string[] = [
  PageTypeAdmin.Dashboard,
  PageTypeAdmin.Availability,
  PageTypeAdmin.TimeClock,
  PageTypeAdmin.PayrollHours,
  PageTypeAdmin.Attendance,
  PageTypeAdmin.Notifications,
  PageTypeAdmin.Tasks,
  PageTypeAdmin.Resources,
  PageTypeAdmin.Password,
];

export function canAccessAdminFeature(
  user: AppUser | null | undefined,
  feature: AdminFeature,
): boolean {
  if (!user) return false;
  const isAdmin = user.isAdmin ?? false;
  const isStaff = user.isStaff ?? false;
  const isTeacher = user.isTeacher ?? false;

  if (isAdmin) return true;

  switch (feature) {
    case "settings":
    case "facilities":
    case "seasons":
    case "balance":
    case "reports":
      return false;
    case "employeesDetail":
      return false;
    case "employeesEdit":
      return false;
    case "classesEdit":
      return false;
    case "timeClock":
      return isStaff || isTeacher;
    case "privateLessonsCalendar":
      return isStaff || isTeacher;
    default:
      return false;
  }
}

/** Class attendance session — Staff may cover a substitute from the class page. */
export function isAttendanceClassDetailPath(pathname: string): boolean {
  const path = pathname.split("?")[0] ?? pathname;
  return /^\/admin\/dashboard\/attendance\/\d+$/.test(path);
}

/** Whether the current path is allowed for staff. Use in dashboard layout. */
export function isPathAllowedForStaff(pathname: string): boolean {
  if (!pathname.startsWith("/admin")) return true;
  if (isAttendanceClassDetailPath(pathname)) return true;

  for (const pattern of STAFF_FORBIDDEN_PATH_PATTERNS) {
    if (pattern.test(pathname)) return false;
  }
  for (const prefix of STAFF_FORBIDDEN_PATH_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return false;
  }
  for (const prefix of STAFF_ALLOWED_PATH_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) return true;
  }
  return false;
}

export function isStaffUser(user: AppUser | null | undefined): boolean {
  return (user?.isStaff ?? false) && !(user?.isAdmin ?? false);
}

/** Whether the current path is allowed for teacher. Use in dashboard layout guard. */
export function isPathAllowedForTeacher(pathname: string): boolean {
  if (!pathname.startsWith("/admin")) return true;
  if (isAttendanceClassDetailPath(pathname)) return true;
  for (const prefix of TEACHER_ALLOWED_PATH_PREFIXES) {
    // Dashboard: only exact path /admin/dashboard (no access to /admin/dashboard/settings etc.)
    if (prefix === PageTypeAdmin.Dashboard) {
      if (pathname === prefix) return true;
    } else if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return true;
    }
  }
  return false;
}

export function isTeacherUser(user: AppUser | null | undefined): boolean {
  return (user?.isTeacher ?? false) && !(user?.isAdmin ?? false);
}

export type AdminDashboardAccessFlags = AdminRoleFlags & {
  activeRole?: string | null;
  choreographerPortalEnabled?: boolean;
  contractorsEnabled?: boolean;
  ticketsEnabled?: boolean;
};

function isDancesPath(pathname: string): boolean {
  return (
    pathname === PageTypeAdmin.Dances ||
    pathname.startsWith(`${PageTypeAdmin.Dances}/`)
  );
}

function isContractorsPath(pathname: string): boolean {
  return (
    pathname === PageTypeAdmin.Contractors ||
    pathname.startsWith(`${PageTypeAdmin.Contractors}/`)
  );
}

function isTicketsPath(pathname: string): boolean {
  return (
    pathname === PageTypeAdmin.Tickets ||
    pathname.startsWith(`${PageTypeAdmin.Tickets}/`)
  );
}

function isOwnPasswordPath(pathname: string): boolean {
  return (
    pathname === PageTypeAdmin.Password ||
    pathname.startsWith(`${PageTypeAdmin.Password}/`)
  );
}

function canChangeOwnPassword(role: string | null): boolean {
  return (
    role === UserRoles.Staff ||
    role === UserRoles.Teacher ||
    role === UserRoles.Choreographer
  );
}

/** Choreographer portal: dashboard, password, and Dances only while the plugin is on. */
export function isPathAllowedForChoreographer(
  pathname: string,
  choreographerPortalEnabled: boolean,
): boolean {
  if (!pathname.startsWith("/admin")) return true;
  if (pathname === PageTypeAdmin.Dashboard || isOwnPasswordPath(pathname))
    return true;
  if (!choreographerPortalEnabled) return false;
  return isDancesPath(pathname);
}

/** Resolves the effective admin-dashboard role (respects role switch). */
export function getEffectiveAdminDashboardRole(
  flags: AdminDashboardAccessFlags,
): string | null {
  if (flags.activeRole) return flags.activeRole;
  if (flags.isAdmin) return UserRoles.Admin;
  if (flags.isTeacher) return UserRoles.Teacher;
  if (flags.isStaff) return UserRoles.Staff;
  if (flags.isChoreographer) return UserRoles.Choreographer;
  return null;
}

/** Whether the path is allowed for the user's active admin-dashboard role. */
export function isAdminDashboardPathAllowed(
  pathname: string,
  flags: AdminDashboardAccessFlags,
): boolean {
  if (!pathname.startsWith(PageTypeAdmin.Dashboard)) return true;

  const role = getEffectiveAdminDashboardRole(flags);
  const portalOn = flags.choreographerPortalEnabled === true;
  const contractorsOn = flags.contractorsEnabled === true;
  const ticketsOn = flags.ticketsEnabled === true;
  if (isOwnPasswordPath(pathname)) return canChangeOwnPassword(role);
  if (isDancesPath(pathname) && !portalOn) return false;
  if (isContractorsPath(pathname) && !contractorsOn) return false;
  if (isTicketsPath(pathname) && !ticketsOn) return false;
  if (role === UserRoles.Admin) return true;
  if (role === UserRoles.Staff) return isPathAllowedForStaff(pathname);
  if (role === UserRoles.Teacher) return isPathAllowedForTeacher(pathname);
  if (role === UserRoles.Choreographer) {
    return isPathAllowedForChoreographer(pathname, portalOn);
  }
  return false;
}

/** Only admins acting as Admin may delete parent accounts (never staff). */
export function canDeleteParentAccount(
  user: AppUser | null | undefined,
): boolean {
  return isUserActingAsAdmin(user);
}

/** Create, edit, and delete waivers/policies - admin only. Staff may preview. */
export function canManagePolicies(user: AppUser | null | undefined): boolean {
  return isUserActingAsAdmin(user);
}

/** Only admins acting as Admin may delete waivers/policies (never staff). */
export function canDeletePolicy(user: AppUser | null | undefined): boolean {
  return canManagePolicies(user);
}

/** Only admins acting as Admin may delete students (never staff). */
export function canDeleteStudent(user: AppUser | null | undefined): boolean {
  return isUserActingAsAdmin(user);
}

/** Force-enroll a student into a class, skipping waitlist and capacity. */
export function canForceEnrollStudents(
  user: AppUser | null | undefined,
): boolean {
  return isUserActingAsAdmin(user) || isUserActingAsStaff(user);
}

/** Take class attendance on behalf of the assigned teacher (substitute desk). */
export function canTakeClassAttendance(
  user: AppUser | null | undefined,
): boolean {
  return isUserActingAsAdmin(user) || isUserActingAsStaff(user);
}

/** Review makeup requests — Staff owns the queue; Admin covers in an emergency. */
export function canReviewMakeupRequests(
  user: AppUser | null | undefined,
): boolean {
  return isUserActingAsAdmin(user) || isUserActingAsStaff(user);
}

/** Create, edit, put on sale, close, and delete ticket events. Staff can view and scan. */
export function canManageTicketEvents(
  user: AppUser | null | undefined,
): boolean {
  return isUserActingAsAdmin(user);
}

/** Create and assign user tasks (admin portal only). */
export function canCreateUserTasks(user: AppUser | null | undefined): boolean {
  return isUserActingAsAdmin(user);
}

/** View all studio user tasks (admin portal only). */
export function canViewTenantUserTasks(
  user: AppUser | null | undefined,
): boolean {
  return isUserActingAsAdmin(user);
}

function isUserActingAsStaff(user: AppUser | null | undefined): boolean {
  const role = getEffectiveAdminDashboardRole({
    isAdmin: user?.isAdmin,
    isTeacher: user?.isTeacher,
    isStaff: user?.isStaff,
    activeRole: user?.activeRole,
  });
  if (role !== UserRoles.Staff) return false;
  return user?.isStaff ?? false;
}

function isUserActingAsAdmin(user: AppUser | null | undefined): boolean {
  const role = getEffectiveAdminDashboardRole({
    isAdmin: user?.isAdmin,
    isTeacher: user?.isTeacher,
    isStaff: user?.isStaff,
    activeRole: user?.activeRole,
  });
  if (role !== UserRoles.Admin) return false;
  return user?.isAdmin ?? false;
}
